import moment from 'moment';

import {
  EmployeeCollaborationTask,
  EmployeeCollaborationTaskStatus,
} from '~/data/collaboration';
import { convertToDateOrUndefined, getNumberOrUndefined } from '~/utils/helper';

export type WorklistTaskFormData = {
  taskId?: number;
  seq?: number;
  templateId?: number;
  worklistId?: string;
  title: string;
  description: string;
  link: string;
  dueDate?: Date;
  targetEmployeeId: number;
  responsibleEmployeeId?: number;
  creatorEmployeeId: number;
  taskType: string;
  taskStatus: EmployeeCollaborationTaskStatus;
  startDate?: Date;
  createdTime?: Date;
  updatedTime?: Date;
  updatedBy?: number;
  attachmentId?: number;
  attachmentName: string;
  attachmentIsArchived: boolean;
};

export const convertWorklistTaskToEditTaskData = (
  task: EmployeeCollaborationTask,
): WorklistTaskFormData => {
  const {
    fTaskId,
    fTaskSeq,
    taskCore: {
      fTaskTitle,
      fTaskDescription,
      fTaskTemplateId,
      fTaskPertainingToId,
      fTaskAssigneeId,
      fTaskAssignorId,
      fTaskType,
      fTaskWorklistUid,
      fTaskLink,
    },
    taskState: { fTaskDueDate, fTaskStatus, fTaskStartDate },
    taskAudit: { fTaskCreatedTime, fTaskUpdatedEmpId, fTaskUpdatedTime },
    fTaskAttachmentId,
    fTaskAttachmentName,
    fTaskAttachmentIsArchived,
  } = task;

  return {
    taskId: getNumberOrUndefined(fTaskId),
    seq: fTaskSeq,
    taskType: fTaskType,
    taskStatus: fTaskStatus,
    startDate: convertToDateOrUndefined(fTaskStartDate),
    templateId: fTaskTemplateId,
    worklistId: fTaskWorklistUid,
    title: fTaskTitle,
    description: fTaskDescription,
    targetEmployeeId: fTaskPertainingToId,
    dueDate: convertToDateOrUndefined(fTaskDueDate),
    responsibleEmployeeId: fTaskAssigneeId,
    creatorEmployeeId: fTaskAssignorId,
    createdTime: convertToDateOrUndefined(fTaskCreatedTime),
    updatedTime: convertToDateOrUndefined(fTaskUpdatedTime),
    updatedBy: getNumberOrUndefined(fTaskUpdatedEmpId),
    link: fTaskLink ?? '',
    attachmentId: fTaskAttachmentId,
    attachmentName: fTaskAttachmentName,
    attachmentIsArchived: !!fTaskAttachmentIsArchived,
  };
};

export const convertEditTaskDataToSubmitPayload = (
  data: WorklistTaskFormData,
  overrideWorklistId?: string,
): EmployeeCollaborationTask => {
  const {
    taskId,
    seq,
    taskType,
    taskStatus,
    templateId,
    worklistId,
    title,
    description,
    startDate,
    dueDate,
    targetEmployeeId,
    responsibleEmployeeId,
    creatorEmployeeId,
    createdTime,
    updatedTime,
    updatedBy,
    link,
    attachmentId,
    attachmentName,
    attachmentIsArchived,
  } = data;

  const formatDate = (date?: Date): string => {
    return !!date ? moment(date).format('YYYY-MM-DD') : '';
  };

  return {
    fTaskId: !!taskId ? taskId.toString() : 'new',
    fTaskSeq: seq,
    taskCore: {
      fTaskTitle: title,
      fTaskDescription: description,
      fTaskTemplateId: templateId,
      fTaskWorklistUid: !!overrideWorklistId ? overrideWorklistId : worklistId,
      fTaskPertainingToId: targetEmployeeId,
      fTaskAssigneeId: responsibleEmployeeId,
      fTaskAssignorId: creatorEmployeeId,
      fTaskType: taskType,
      fTaskIsVisibleToPertainingTo: true,
      fTaskLink: link,
    },
    taskState: {
      fTaskStartDate: formatDate(startDate),
      fTaskDueDate: formatDate(dueDate),
      fTaskStatus: taskStatus,
    },
    taskAudit: {
      fTaskCreatedTime: formatDate(createdTime),
      fTaskUpdatedTime: formatDate(updatedTime),
      fTaskUpdatedEmpId: updatedBy,
    },
    fTaskAttachmentId: attachmentId ?? -1,
    fTaskAttachmentName: attachmentName,
    fTaskAttachmentIsArchived: attachmentIsArchived,
  };
};
